import { computed, onScopeDispose, readonly, ref, watch } from 'vue'
import { PortalApi } from '../api/portalApi'
import { portalPayUrl } from '../services/portalSafety'
import type { PortalFlowItem } from '../types/portal'

function classify(error: unknown): string {
  const failure = error as {
    response?: { status?: number }
    status?: number
    message?: string
  } | null
  const status = failure?.response?.status ?? failure?.status
  if (status === 401) return 'portal.errors.auth'
  if (status === 403 || status === 404) return 'portal.errors.notFound'
  if (failure?.message === 'PORTAL_INVALID_RESPONSE')
    return 'portal.errors.response'
  return 'portal.errors.status'
}

/** Read-only recovery. Owner changes reload from the server, never browser storage. */
export function usePortalFlows(owner: () => number | null, scope: () => string = () => '', onAuthFailure?: () => void, preserveHistory = false, autoReload = true) {
  const items = ref<PortalFlowItem[]>([])
  const nextCursor = ref<string | null>(null)
  const loading = ref(false)
  const loadingMore = ref(false)
  const loaded = ref(false)
  const errorKey = ref('')
  const statusErrors = ref<Record<string, string>>({})
  const refreshingIds = ref<string[]>([])
  const staleIds = ref<string[]>([])
  const listStale = ref(false)
  const flowAvailable = ref(false)
  let controller = new AbortController()
  let disposed = false
  const refreshedSinceDiscovery = new Set<string>()

  const hasMore = computed(() => !listStale.value && nextCursor.value !== null)
  const empty = computed(
    () =>
      loaded.value &&
      !loading.value &&
      !errorKey.value &&
      Object.keys(statusErrors.value).length === 0 &&
      items.value.length === 0,
  )

  function cancel() {
    controller.abort()
    controller = new AbortController()
    loading.value = false
    loadingMore.value = false
    refreshingIds.value = []
  }

  async function loadPage(cursor?: string, merge = false) {
    if (disposed || !owner() || loading.value || loadingMore.value) return
    const signal = controller.signal
    const expectedOwner = owner()
    const current = () => !disposed && !signal.aborted && owner() === expectedOwner
    if (cursor === undefined) loading.value = true
    else loadingMore.value = true
    errorKey.value = ''
    const previousItems = new Map(items.value.map(item => [item.attempt_id, item]))
    try {
      const page = await PortalApi.flowList(cursor, signal)
      if (!current()) return
      flowAvailable.value = page.flow_available === true
      if (cursor === undefined && !merge) {
        const ids = new Set(page.items.map(item => item.attempt_id))
        items.value = preserveHistory ? [...page.items, ...items.value.filter(item => !ids.has(item.attempt_id))] : page.items
        staleIds.value = preserveHistory ? staleIds.value.filter(id => !ids.has(id)) : []
        statusErrors.value = {}
        listStale.value = false
      } else {
        // An overlapping page must not erase a more recently refreshed invoice.
        const known = new Set(items.value.map((item) => item.attempt_id))
        for (const item of page.items) {
          if (!known.has(item.attempt_id)) {
            items.value.push(item)
            known.add(item.attempt_id)
          } else if (!refreshedSinceDiscovery.has(item.attempt_id) && !statusErrors.value[item.attempt_id] && !refreshingIds.value.includes(item.attempt_id)) {
            items.value = items.value.map(previous => previous.attempt_id === item.attempt_id && previous === previousItems.get(item.attempt_id) ? item : previous)
          }
        }
        refreshedSinceDiscovery.clear()
      }
      nextCursor.value = page.next_cursor
      loaded.value = true
    } catch (error) {
      if (!current()) return
      flowAvailable.value = false
      if ([401, 403].includes((error as { response?: { status?: number } }).response?.status ?? 0)) onAuthFailure?.()
      errorKey.value = classify(error)
      if (
        ['portal.errors.auth', 'portal.errors.notFound'].includes(errorKey.value)
      ) {
        cancel()
        items.value = []
        nextCursor.value = null
        loaded.value = false
      }
    } finally {
      if (current()) {
        loading.value = false
        loadingMore.value = false
      }
    }
  }

  async function reload() {
    if (disposed) return
    cancel()
    flowAvailable.value = false
    // Keep same-owner history visible, but do not trust its payment links or
    // pagination cursor until the server revalidates them.
    staleIds.value = items.value.map(item => item.attempt_id)
    listStale.value = true
    errorKey.value = ''
    statusErrors.value = {}
    await loadPage()
  }

  async function loadMore() {
    if (!listStale.value && nextCursor.value !== null) await loadPage(nextCursor.value)
  }

  async function refreshStatus(attemptId: string) {
    if (
      disposed ||
      !owner() ||
      loading.value ||
      refreshingIds.value.includes(attemptId) ||
      !items.value.some((item) => item.attempt_id === attemptId)
    )
      return
    const signal = controller.signal
    const expectedOwner = owner()
    const current = () => !disposed && !signal.aborted && owner() === expectedOwner
    refreshingIds.value.push(attemptId)
    try {
      const item = await PortalApi.flowStatus(attemptId, signal)
      if (!current()) return
      items.value = items.value.map((previous) =>
        previous.attempt_id === attemptId ? item : previous,
      )
      refreshedSinceDiscovery.add(attemptId)
      delete statusErrors.value[attemptId]
      staleIds.value = staleIds.value.filter(id => id !== attemptId)
    } catch (error) {
      if (!current()) return
      flowAvailable.value = false
      const key = classify(error)
      if ([401, 403].includes((error as { response?: { status?: number } }).response?.status ?? 0)) onAuthFailure?.()
      statusErrors.value[attemptId] = key
      if (!staleIds.value.includes(attemptId)) staleIds.value.push(attemptId)
      if (key === 'portal.errors.auth') {
        cancel()
        items.value = []
        nextCursor.value = null
        loaded.value = false
        errorKey.value = key
      } else if (key === 'portal.errors.notFound') {
        items.value = items.value.filter((item) => item.attempt_id !== attemptId)
      }
    } finally {
      if (current())
        refreshingIds.value = refreshingIds.value.filter((id) => id !== attemptId)
    }
  }

  async function discover() {
    await loadPage(nextCursor.value ?? undefined, true)
  }

  function clear() {
    cancel()
    refreshedSinceDiscovery.clear()
    items.value = []
    nextCursor.value = null
    loaded.value = false
    statusErrors.value = {}
    staleIds.value = []
    errorKey.value = ''
  }

  function remember(item: PortalFlowItem) {
    const index = items.value.findIndex(value => value.attempt_id === item.attempt_id)
    if (index < 0) items.value.push(item)
    else items.value[index] = item
  }

  function payUrl(attemptId: string): string | null {
    const item = items.value.find((value) => value.attempt_id === attemptId)
    if (
      !item ||
      staleIds.value.includes(attemptId) ||
      statusErrors.value[attemptId] ||
      !['preparing', 'kyc_required', 'awaiting_payment'].includes(item.status)
    )
      return null
    return portalPayUrl(item.pay_url)
  }

  watch(() => [owner(), scope()], () => {
    items.value = []
    nextCursor.value = null
    loaded.value = false
    if (autoReload) void reload()
    else cancel()
  }, { immediate: true, flush: 'sync' })
  onScopeDispose(() => {
    disposed = true
    cancel()
  })

  return {
    remember,
    cancel,
    clear,
    discover,
    flowAvailable: readonly(flowAvailable),
    items: readonly(items),
    nextCursor: readonly(nextCursor),
    loaded: readonly(loaded),
    loading: readonly(loading),
    loadingMore: readonly(loadingMore),
    errorKey: readonly(errorKey),
    statusErrors: readonly(statusErrors),
    refreshingIds: readonly(refreshingIds),
    empty,
    hasMore,
    reload,
    loadMore,
    refreshStatus,
    payUrl,
  }
}
